'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { createClient } from '@/lib/supabase/client'
import { buscarMeuPerfil } from '@/lib/perfil/meu-perfil'
import TransparentCheckout from '@/components/ui/transparent-checkout'

const modules = [
  {
    id: 'M0',
    title: 'Degustação: O Mundo Mudou',
    aulas: 4,
    desc: 'O que aconteceu com o Google, por que 58,5% das buscas não geram mais cliques e o que isso significa para o seu negócio.',
    free: true,
  },
  {
    id: 'M1',
    title: 'Como a IA Responde Perguntas',
    aulas: 5,
    desc: 'Entenda como o ChatGPT, Gemini e AI Overviews decidem quais negócios recomendar. E por que o seu pode estar invisível.',
  },
  {
    id: 'M2',
    title: 'Os 3 Pilares da Visibilidade Moderna',
    aulas: 5,
    desc: 'SEO técnico, GEO (Generative Engine Optimization) e Brand Search. Os três eixos que definem quem aparece na era da IA.',
  },
  {
    id: 'M3',
    title: 'Google Meu Negócio: Sua Base de Operações',
    aulas: 5,
    desc: 'Configure, otimize e transforme seu perfil no Google em uma máquina de captação. Passo a passo para empresários.',
  },
  {
    id: 'M4',
    title: 'Conteúdo que a IA Recomenda',
    aulas: 5,
    desc: 'Formatos, estruturas e práticas que fazem a IA citar seu negócio. Baseado em pesquisa de Princeton e KDD 2024.',
  },
  {
    id: 'M5',
    title: 'Seu Plano de Ação Completo',
    aulas: 4,
    desc: 'Diagnóstico, priorização e cronograma personalizado. Saia do curso com um plano pronto para executar.',
  },
  {
    id: 'M6',
    title: 'Bônus: Cases e Conexões',
    aulas: 4,
    desc: 'Estudos de caso reais, templates prontos e acesso à comunidade de empresários aplicando SEO/GEO.',
  },
]

const faqs = [
  {
    q: 'Preciso saber sobre tecnologia ou programação?',
    a: 'Não. O curso foi desenhado para empresários e empreendedores sem conhecimento técnico. Todo conteúdo é explicado com linguagem acessível e exemplos práticos do dia a dia.',
  },
  {
    q: 'O que é GEO?',
    a: 'GEO significa Generative Engine Optimization. É a prática de otimizar seu conteúdo para que IAs como ChatGPT, Gemini e Perplexity recomendem seu negócio quando alguém faz uma pergunta. É o "novo SEO" da era da inteligência artificial.',
  },
  {
    q: 'Meu negócio é local. Isso funciona para mim?',
    a: 'Especialmente para negócios locais. O módulo 3 inteiro é dedicado ao Google Meu Negócio, que é a ferramenta mais poderosa para empresas com endereço físico ou que atendem regiões específicas.',
  },
  {
    q: 'Quanto tempo leva para ver resultados?',
    a: 'As primeiras otimizações do Google Meu Negócio podem gerar resultados em dias. SEO orgânico leva de 3 a 6 meses. GEO é mais recente e os primeiros resultados aparecem em 4 a 8 semanas. O módulo 5 te ajuda a montar um cronograma realista.',
  },
  {
    q: 'As aulas ficam disponíveis por quanto tempo?',
    a: 'Acesso vitalício. Assista no seu ritmo, quantas vezes quiser. As aulas são em áudio profissional, então você pode ouvir no carro, na academia ou enquanto trabalha.',
  },
  {
    q: 'Qual a diferença entre este curso e contratar uma agência de SEO?',
    a: 'Agências cobram de R$ 2.000 a R$ 10.000 por mês e você fica dependente delas. Com este curso, você entende o jogo, toma decisões melhores e pode executar as ações essenciais por conta própria ou supervisionar quem faz.',
  },
  {
    q: 'E se eu não gostar?',
    a: 'Garantia incondicional de 7 dias. Pediu reembolso, devolvemos 100%. Sem burocracia.',
  },
]

const dataPoints = [
  {
    number: '58,5%',
    label: 'das buscas no Google terminam sem nenhum clique',
    source: 'SparkToro 2024',
  },
  {
    number: '-58%',
    label: 'de queda no CTR da posição 1 com AI Overviews',
    source: 'Ahrefs 2025',
  },
  {
    number: '+40%',
    label: 'de aumento em visibilidade com técnicas de GEO',
    source: 'Princeton/KDD 2024',
  },
  {
    number: '83%',
    label: 'de correlação entre Brand Search e market share',
    source: 'Les Binet/IPA',
  },
]

export default function PreSeoGeoPage() {
  const [faqOpen, setFaqOpen] = useState<number | null>(null)
  const [showCheckout, setShowCheckout] = useState(false)
  const [checkoutError, setCheckoutError] = useState('')
  const [buyerName, setBuyerName] = useState<string | undefined>()
  const [buyerEmail, setBuyerEmail] = useState<string | undefined>()
  const [buyerPhone, setBuyerPhone] = useState<string | undefined>()
  const [buyerCpf, setBuyerCpf] = useState<string | undefined>()
  const router = useRouter()

  useEffect(() => {
    const supabase = createClient()
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setBuyerEmail(user.email || undefined)
        setBuyerName(user.user_metadata?.full_name || undefined)
        buscarMeuPerfil()
          .then(({ data: profile }) => {
            if (profile) {
              if (profile.full_name) setBuyerName(profile.full_name)
              if (profile.phone) setBuyerPhone(profile.phone)
              if (profile.document) setBuyerCpf(profile.document)
            }
          })
      }
    })
  }, [])

  function handleBuy() {
    setShowCheckout(true)
    setCheckoutError('')
  }

  function scrollToCheckout() {
    const el = document.getElementById('pricing')
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Barra topo */}
      <div className="bg-emerald-600 text-white text-center py-2 px-3 text-xs sm:text-sm font-medium">
        32 aulas. 7 módulos. Módulo 0 gratuito. Pagamento único de R$ 297.
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-emerald-900/40 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 pt-10 pb-12 sm:pt-16 sm:pb-20">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 rounded-full text-[11px] sm:text-xs font-semibold mb-5 sm:mb-6 uppercase tracking-wide">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              Curso completo - SEO + GEO para empresários
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
              Pré-SEO/GEO:{' '}
              <span className="text-emerald-400">Visibilidade na Era da IA</span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-6 sm:mb-8 leading-relaxed max-w-2xl mx-auto">
              O Google mudou. A IA responde antes de você clicar. Aprenda o que fazer para que seu negócio continue sendo encontrado e recomendado.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8">
              <button onClick={handleBuy}
                className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-emerald-600/30 cursor-pointer">
                GARANTIR POR R$ 297
              </button>
              <p className="text-xs sm:text-sm text-gray-400">Pagamento único. Acesso vitalício.</p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">32 aulas</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">~6h de conteúdo</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">29 fontes científicas</span>
              <span className="bg-white/5 border border-white/10 px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full text-gray-300">Áudio profissional</span>
            </div>
          </div>
        </div>
      </section>

      {/* O Problema - Dados */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            O jogo da visibilidade online mudou. Você percebeu?
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            A IA está respondendo perguntas antes do clique. Quem não se adaptar vai desaparecer dos resultados. Não é previsão. Já está acontecendo.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5 mb-10 sm:mb-14">
            {dataPoints.map((d, i) => (
              <div key={i} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 sm:p-5 text-center">
                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-emerald-400 mb-2">{d.number}</div>
                <p className="text-xs sm:text-sm text-gray-300 leading-snug mb-2">{d.label}</p>
                <p className="text-[10px] sm:text-xs text-gray-500 font-mono">{d.source}</p>
              </div>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              {
                icon: '🔴',
                title: 'SEO tradicional não basta mais',
                desc: 'Estar na posição 1 do Google não garante cliques. AI Overviews e featured snippets estão roubando o tráfego que era seu.',
              },
              {
                icon: '🔴',
                title: 'A IA não recomenda quem não conhece',
                desc: 'ChatGPT e Gemini recomendam negócios que têm autoridade, reputação e conteúdo estruturado. Se você não tem, eles indicam o concorrente.',
              },
              {
                icon: '🔴',
                title: 'Google Meu Negócio negligenciado',
                desc: 'A maioria dos empresários deixa o perfil no piloto automático. Quem otimiza aparece no mapa, nas buscas locais e nas respostas da IA.',
              },
              {
                icon: '🔴',
                title: 'Dependência de agências caras',
                desc: 'Você paga R$ 3.000 por mês para alguém fazer o que você poderia entender e supervisionar. Sem saber o básico, não tem como cobrar resultados.',
              },
            ].map((item, i) => (
              <div key={i} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 sm:p-6">
                <div className="text-2xl mb-3">{item.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-white mb-1.5">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Para quem é / não é */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Para quem é este curso?
          </h2>

          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-green-500">&#10003;</span> Para quem é
              </h3>
              <div className="space-y-3">
                {[
                  'Donos de negócios que dependem de clientes vindos do Google',
                  'Empresários que perceberam que o tráfego orgânico caiu e não sabem por quê',
                  'Profissionais liberais que querem ser encontrados por novos clientes',
                  'Empreendedores que querem entender SEO/GEO sem linguagem técnica',
                  'Gestores que precisam cobrar resultados de agências com propriedade',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-green-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-5 flex items-center gap-2">
                <span className="text-red-500">&#10005;</span> Para quem não é
              </h3>
              <div className="space-y-3">
                {[
                  'Especialistas de SEO que já dominam GEO e AI Overviews',
                  'Quem busca curso avançado de programação ou código',
                  'Empresas que não têm presença digital nenhuma (comece pelo básico)',
                  'Quem espera resultado mágico sem aplicar o conteúdo',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 items-start">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
                    <p className="text-sm text-gray-300">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Módulos */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-3">
            O que você vai aprender
          </h2>
          <p className="text-sm sm:text-base text-gray-400 text-center mb-8 sm:mb-12 max-w-2xl mx-auto">
            32 aulas organizadas em 7 módulos. Do diagnóstico ao plano de ação.
          </p>

          <div className="space-y-3 sm:space-y-4">
            {modules.map((m) => (
              <div key={m.id} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-5 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <span className="text-emerald-400 font-mono text-xs bg-emerald-500/10 px-2.5 py-1 rounded shrink-0 mt-0.5">
                    {m.id}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="text-base sm:text-lg font-bold text-white">{m.title}</h3>
                      {m.free && (
                        <span className="text-[10px] sm:text-xs bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded-full font-semibold uppercase">
                          Gratuito
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{m.desc}</p>
                  </div>
                  <span className="text-gray-500 text-xs shrink-0 mt-1">{m.aulas} aulas</span>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mt-6 sm:mt-8">
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            32 aulas - ~6h de conteúdo em áudio profissional
          </div>
        </div>
      </section>

      {/* Sobre o instrutor */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Sobre o instrutor
          </h2>

          <div className="bg-gray-800/40 border border-gray-700/40 rounded-2xl p-6 sm:p-8 md:p-10">
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-emerald-500/20 rounded-full flex items-center justify-center shrink-0 mx-auto sm:mx-0">
                <span className="text-3xl sm:text-4xl font-bold text-emerald-400">DA</span>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-1 text-center sm:text-left">Denis Alves</h3>
                <p className="text-sm text-emerald-400 mb-4 text-center sm:text-left">Estrategista de SEO e GEO</p>
                <div className="space-y-3 text-sm text-gray-300 leading-relaxed">
                  <p>
                    Denis é consultor de SEO com mais de uma década de experiência ajudando empresas a serem encontradas no Google. Quando a IA começou a mudar as regras do jogo, ele mergulhou na pesquisa acadêmica para entender o que estava acontecendo.
                  </p>
                  <p>
                    Compilou 29 fontes internacionais de instituições como Princeton, Stanford, KDD e NeurIPS para criar um curso que traduz a ciência para a linguagem do empresário. Sem jargão técnico. Sem firula. Só o que funciona.
                  </p>
                  <p>
                    Autor, palestrante e defensor de que empresários precisam entender o jogo da visibilidade. Não para virar técnicos, mas para tomar decisões melhores.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Fundamentação científica */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3">
            Fundamentação científica
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mb-8 sm:mb-12 max-w-2xl mx-auto">
            Este não é um curso de "dicas e truques". Cada módulo é baseado em pesquisa publicada e dados verificáveis.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 mb-8 sm:mb-10">
            {[
              { number: '29', label: 'fontes internacionais' },
              { number: '6', label: 'conferências acadêmicas' },
              { number: '12', label: 'estudos de mercado' },
              { number: '4', label: 'universidades citadas' },
            ].map((s, i) => (
              <div key={i} className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 sm:p-5">
                <div className="text-2xl sm:text-3xl font-black text-emerald-400 mb-1">{s.number}</div>
                <p className="text-xs sm:text-sm text-gray-400">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 text-xs sm:text-sm">
            {['KDD 2024', 'NeurIPS', 'Nature', 'Stanford', 'Princeton', 'Ahrefs', 'SparkToro', 'IPA/WARC', 'Rand Fishkin', 'Les Binet'].map((src) => (
              <span key={src} className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 px-3 py-1 sm:px-4 sm:py-1.5 rounded-full">
                {src}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-xl mx-auto">
          <div className="bg-gradient-to-b from-emerald-950/60 to-gray-900/60 border border-emerald-500/30 rounded-2xl p-6 sm:p-10 text-center">
            <div className="text-xs sm:text-sm text-emerald-300 font-semibold uppercase tracking-wide mb-4">
              Pré-SEO/GEO - Visibilidade na Era da IA
            </div>

            <div className="mb-1">
              <span className="text-4xl sm:text-5xl md:text-6xl font-black text-white">R$ 297</span>
            </div>
            <p className="text-sm text-gray-400 mb-6 sm:mb-8">
              Pagamento único. Sem mensalidade. Acesso vitalício.
            </p>

            <button onClick={handleBuy}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 px-8 rounded-xl text-lg transition shadow-lg shadow-emerald-600/30 mb-4 cursor-pointer">
              QUERO O CURSO POR R$ 297
            </button>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-400">
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                Pagamento seguro
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                PIX, cartão ou boleto
              </span>
              <span className="flex items-center justify-center gap-1.5">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                7 dias de garantia
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* O que inclui */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            O que você recebe
          </h2>

          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {[
              { icon: '📚', text: '32 aulas em áudio profissional (Google TTS Chirp3-HD)' },
              { icon: '🎯', text: 'Módulo 0 gratuito para você experimentar antes de comprar' },
              { icon: '📊', text: 'Dados atualizados de 29 fontes internacionais' },
              { icon: '🗺️', text: 'Guia completo de Google Meu Negócio passo a passo' },
              { icon: '📋', text: 'Plano de ação personalizado no módulo final' },
              { icon: '🔬', text: 'Técnicas de GEO baseadas em pesquisa de Princeton e KDD' },
              { icon: '💼', text: 'Cases reais de empresas que aplicaram SEO/GEO' },
              { icon: '♾️', text: 'Acesso vitalício com atualizações incluídas' },
            ].map((item, i) => (
              <div key={i} className="flex gap-3 items-start bg-gray-800/30 border border-gray-700/30 rounded-xl p-4">
                <span className="text-xl shrink-0">{item.icon}</span>
                <p className="text-sm text-gray-300">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-8 sm:mb-12">
            Perguntas frequentes
          </h2>

          <div className="space-y-3">
            {faqs.map((item, i) => (
              <div key={i} className="bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden">
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex justify-between items-center p-4 sm:p-5 text-left cursor-pointer"
                >
                  <span className="text-sm sm:text-base font-medium text-white pr-4">{item.q}</span>
                  <svg className={`w-5 h-5 text-gray-400 shrink-0 transition-transform ${faqOpen === i ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {faqOpen === i && (
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-sell: Combo SEO/GEO */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-r from-emerald-950/60 to-blue-950/60 border border-emerald-500/20 rounded-2xl p-6 sm:p-8 md:p-10">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/15 border border-yellow-500/25 text-yellow-300 rounded-full text-[11px] font-semibold mb-4 uppercase">
                Oferta especial
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">
                Combo SEO/GEO: Pré + Avançado
              </h3>
              <p className="text-sm sm:text-base text-gray-300 mb-4 max-w-xl mx-auto">
                Junte o Pré-SEO/GEO com o curso avançado e leve os dois por um preço menor que comprar separado.
              </p>

              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="text-gray-500 line-through text-lg">De R$ 594</span>
                <span className="text-3xl sm:text-4xl font-black text-white">R$ 497</span>
              </div>

              <a href="/combo-seo-geo"
                className="inline-block bg-yellow-500 hover:bg-yellow-400 text-gray-900 font-bold py-3 sm:py-3.5 px-8 sm:px-10 rounded-xl text-base transition shadow-lg mb-3">
                VER COMBO SEO/GEO
              </a>
              <p className="text-xs text-gray-500">Economize R$ 97 levando os dois cursos juntos.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-950">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Seu negócio pode continuar invisível. Ou pode aprender a jogar o novo jogo.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mb-6 sm:mb-8">
            32 aulas. Do diagnóstico ao plano de ação. R$ 297.
          </p>

          <button onClick={handleBuy}
            className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3.5 sm:py-4 px-8 sm:px-10 rounded-xl text-base sm:text-lg transition shadow-lg shadow-emerald-600/30 mb-4 cursor-pointer">
            GARANTIR MEU ACESSO AGORA
          </button>
          <p className="text-xs text-gray-500">Pagamento seguro via Assas. PIX, cartão ou boleto.</p>
        </div>
      </section>

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowCheckout(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Finalizar compra</h3>
                <p className="text-sm text-gray-500">Pré-SEO/GEO - Visibilidade na Era da IA</p>
              </div>
              <button onClick={() => setShowCheckout(false)} className="text-gray-400 hover:text-gray-600 p-1 cursor-pointer">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6">
              {checkoutError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {checkoutError}
                </div>
              )}
              <TransparentCheckout
                channelSlug="pre-seo-geo"
                buyerName={buyerName}
                buyerEmail={buyerEmail}
                buyerPhone={buyerPhone}
                buyerCpf={buyerCpf}
                onSuccess={(paymentId) => {
                  router.push('/obrigado?payment=' + paymentId)
                }}
                onError={(error) => {
                  setCheckoutError(error)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 sm:py-8 px-4 bg-gray-950 border-t border-gray-800 text-center">
        <p className="text-xs sm:text-sm text-gray-500">
          Pré-SEO/GEO - Visibilidade na Era da IA
        </p>
        <p className="text-xs text-gray-600 mt-1">eBuz Negócios Digitais LTDA - CNPJ 29.436.457/0001-25</p>
      </footer>
    </div>
  )
}
